import React, { useState } from 'react';
import { Link } from 'react-router-dom';
// Importamos el helper para los estilos de clasificación
import { getClasificacionChip } from '../../../utils/styles';

const TablaClientes = ({ clients, onEdit, onDelete }) => {
  const recordsPerPage = 10; 
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalRecords = clients.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedClients = clients.slice(startIndex, startIndex + recordsPerPage);

  // --- HELPERS DE EXTRACCIÓN DE DATOS ---
  // Estos helpers aseguran la coexistencia: priorizan el nuevo array de strings
  // pero muestran el dato del campo plano si el array no está disponible.

  const getDisplayEmail = (client) => {
      // 1. Buscamos en el array de emails (nuevo formato de strings)
      if (client.emails && Array.isArray(client.emails)) {
          const emailFromArray = client.emails.find(e => typeof e === 'string' && e.trim() !== '');
          if (emailFromArray) return emailFromArray;
      }
      // 2. Fallback al campo plano legacy
      return client.email || null;
  };

  const getDisplayPhone = (client) => {
      // 1. Buscamos en el array de telefonos (nuevo formato de strings)
      if (client.telefonos && Array.isArray(client.telefonos)) {
          const phoneFromArray = client.telefonos.find(t => typeof t === 'string' && t.trim() !== '');
          if (phoneFromArray) return phoneFromArray;
      }
      // 2. Fallback al campo plano legacy
      return client.telefono || null;
  };

  const renderPagination = () => {
    if (totalRecords === 0) return null;
    
    return (
      <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-100 px-6 py-4 bg-gray-50 rounded-b-xl">
        <p className="text-sm text-gray-600 mb-2 sm:mb-0">
          Mostrando <span className="font-bold text-gray-800">{startIndex + 1}</span> a <span className="font-bold text-gray-800">{Math.min(startIndex + recordsPerPage, totalRecords)}</span> de <span className="font-bold text-gray-800">{totalRecords}</span> resultados
        </p>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Anterior
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Siguiente
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Contenedor con Scroll para la tabla */}
      <div className="flex-grow overflow-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[200px]">
                Nombre / Razón Social
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[150px]">
                Apellido
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[140px]">
                Clasificación
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[120px]">
                DNI
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[130px]">
                CUIT
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[130px]">
                Teléfono
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[200px]">
                E-mail
              </th>
              <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[120px] sticky right-0 bg-gray-50 shadow-l-md">
                Acción
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedClients.length === 0 ? (
                <tr>
                    <td colSpan="8" className="px-6 py-10 text-center text-gray-400 italic">
                        No hay clientes para mostrar
                    </td>
                </tr>
            ) : (
                paginatedClients.map((client) => {
                const displayEmail = getDisplayEmail(client);
                const displayPhone = getDisplayPhone(client);

                return (
                <tr key={client.id} className="hover:bg-blue-50/50 transition-colors duration-150 group">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800">
                        {client.nombre || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                        {client.apellido || '-'}
                    </td>
                    
                    {/* Chip de Clasificación */}
                    <td className="px-6 py-4 whitespace-nowrap">
                        {client.clasificacion ? (
                            <span className={`inline-flex items-center justify-center ${getClasificacionChip(client.clasificacion)} shadow-sm`}>
                                {client.clasificacion}
                            </span>
                        ) : (
                            <span className="text-gray-300 text-xs italic">S/D</span>
                        )}
                    </td>

                    {/* DNI */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {client.dni || '-'}
                    </td>
                    {/* CUIT */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {client.cuit || '-'}
                    </td>
                    
                    {/* Teléfono Robustecido */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {displayPhone ? (
                            <a href={`tel:${displayPhone}`} className="hover:text-blue-600 hover:underline">{displayPhone}</a>
                        ) : '-'}
                    </td>
                    
                    {/* Email Robustecido */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {displayEmail ? (
                            <a href={`mailto:${displayEmail}`} className="text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-[200px]" title={displayEmail}>
                                {displayEmail}
                            </a>
                        ) : '-'}
                    </td>
                    
                    {/* Acciones */}
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium sticky right-0 bg-white group-hover:bg-blue-50/50 shadow-l-md border-l border-transparent group-hover:border-blue-100">
                        <div className="flex items-center justify-center space-x-3">
                            <Link 
                                to={`/clientes/${client.id}`} 
                                className="text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-full hover:bg-blue-100" 
                                title="Ver detalles"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                            </Link>
                            
                            <button 
                                onClick={() => onEdit(client)}
                                className="text-gray-400 hover:text-yellow-600 transition-colors p-1 rounded-full hover:bg-yellow-50" 
                                title="Editar"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>
                            </button>
                            
                            <button 
                                onClick={() => onDelete(client.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50" 
                                title="Eliminar"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                            </button>
                        </div>
                    </td>
                </tr>
                );
            }))}
          </tbody>
        </table>
      </div>
      {renderPagination()}
    </div>
  );
};

export default TablaClientes;